
import { useEffect, useState } from 'react';
import { Loader, CloudOff, CheckCircle2, WifiOff } from "lucide-react";
import { useOfflineDetection } from '@/hooks/useOfflineDetection';

interface SyncIndicatorProps {
  isSyncing: boolean;
  lastSyncTime?: number;
  className?: string;
}

export const SyncIndicator = ({ isSyncing, lastSyncTime, className = "" }: SyncIndicatorProps) => {
  const { isOffline, connectionQuality } = useOfflineDetection();
  const [showSuccess, setShowSuccess] = useState(false);
  
  // Show success icon briefly when sync completes
  useEffect(() => {
    if (!isSyncing) {
      setShowSuccess(true);
      const timer = setTimeout(() => setShowSuccess(false), 3000);
      return () => clearTimeout(timer);
    }
    return () => {};
  }, [isSyncing]);
  
  if (isOffline) {
    return (
      <div className={`flex items-center gap-1 text-xs text-yellow-600 ${className}`}>
        <WifiOff size={14} />
        <span>Offline</span>
      </div>
    );
  }
  
  if (connectionQuality === 'poor') {
    return (
      <div className={`flex items-center gap-1 text-xs text-yellow-600 ${className}`}>
        <CloudOff size={14} />
        <span>Poor connection</span>
      </div>
    );
  }
  
  if (isSyncing) {
    return (
      <div className={`flex items-center gap-1 text-xs text-blue-600 ${className}`}>
        <Loader size={14} className="animate-spin" />
        <span>Syncing...</span>
      </div>
    );
  }
  
  if (showSuccess) {
    return (
      <div className={`flex items-center gap-1 text-xs text-green-600 ${className}`}>
        <CheckCircle2 size={14} />
        <span>Synced</span>
      </div>
    );
  }
  
  if (lastSyncTime) {
    const minutes = Math.floor((Date.now() - lastSyncTime) / 60000);
    return (
      <div className={`flex items-center gap-1 text-xs text-gray-500 ${className}`}>
        <CheckCircle2 size={14} />
        <span>Synced {minutes < 1 ? 'just now' : `${minutes}m ago`}</span>
      </div>
    );
  }
  
  return null;
};
